import React, {useEffect, useRef} from "react";
import PropTypes from "prop-types";

const SIMPLYBOOK_SCRIPT_SRC = "https://widget.simplybook.me/v2/widget/widget.js";
const SIMPLYBOOK_SCRIPT_ID = "simplybook-widget-script";

const ensureScript = (src, id) =>
  new Promise((resolve, reject) => {
	  const existing = document.getElementById(id);
	  if (existing) {
		  if (existing.dataset.loaded === "true" || window.SimplybookWidget) {
			  return resolve();
		  }
		  existing.addEventListener("load", () => resolve());
		  existing.addEventListener("error", reject);
		  return;
	  }

	  const script = document.createElement("script");
	  script.id = id;
	  script.src = src;
	  script.async = true;
	  script.onload = () => {
		  script.dataset.loaded = "true";
		  resolve();
	  };
	  script.onerror = reject;
	  document.body.appendChild(script);
  });

// NOTE: We intentionally build a fresh widget instance on every open.
// SimplyBook renders its booking form inside an <iframe>; browsers reload an
// iframe whenever it is moved within the DOM, so re-parenting a cached node
// resets the iframe to SimplyBook's landing page ("Book Now") instead of the
// calendar. Re-instantiating keeps the calendar loading correctly. SimplyBook's
// console warning about multiple instances is cosmetic and safe to ignore.
const WIDGET_CONTAINER_ID = "simplybook-widget-container";

/**
 * Renders the SimplyBook.me booking widget inline into its own container.
 *
 * @param {string} company     SimplyBook.me company login (subdomain).
 * @param {string} theme       Optional widget theme name.
 * @param {Object} themeSettings Optional theme settings overrides.
 * @param {Object} predefined   Preselected values (e.g. {provider, service}).
 */
const SimplyBookWidget = ({
							  company,
							  theme = "",
							  themeSettings = {},
							  predefined = {},
							  timeline = "modern",
							  datepicker = "top_calendar",
							  isRtl = false,
							  appConfig = {}
						  }) => {
	const containerRef = useRef(null);
	const widgetNodeRef = useRef(null);

	useEffect(() => {
		let cancelled = false;

		if (!company) {
			console.error("SimplyBookWidget: missing `company` login.");
			return;
		}

		(async () => {
			try {
				await ensureScript(SIMPLYBOOK_SCRIPT_SRC, SIMPLYBOOK_SCRIPT_ID);

				if (cancelled || !window.SimplybookWidget || !containerRef.current) {
					return;
				}

				// Build a fresh widget node/instance so the iframe loads the calendar
				// from scratch on every open (see note above).
				const node = document.createElement("div");
				node.id = WIDGET_CONTAINER_ID;
				node.style.width = "100%";
				containerRef.current.appendChild(node);
				widgetNodeRef.current = node;

				new window.SimplybookWidget({
					widget_type: "iframe",
					url: `https://${company}.simplybook.it`,
					theme,
					theme_settings: themeSettings,
					timeline,
					datepicker,
					is_rtl: isRtl,
					app_config: {
						...appConfig,
						predefined
					},
					container_id: WIDGET_CONTAINER_ID
				});
			} catch (e) {
				console.error("SimplyBook widget script failed:", e);
			}
		})();

		return () => {
			cancelled = true;
			// Fully remove the widget node so the next open starts clean.
			if (widgetNodeRef.current && widgetNodeRef.current.parentNode) {
				widgetNodeRef.current.parentNode.removeChild(widgetNodeRef.current);
			}
			widgetNodeRef.current = null;
		};
	}, [company, theme, timeline, datepicker, isRtl]);

	return (
	  <div
		ref={containerRef}
		style={{width: "100%", minHeight: "80dvh"}}
	  />
	);
};

SimplyBookWidget.propTypes = {
	company: PropTypes.string.isRequired,
	theme: PropTypes.string,
	themeSettings: PropTypes.object,
	predefined: PropTypes.object,
	timeline: PropTypes.string,
	datepicker: PropTypes.string,
	isRtl: PropTypes.bool,
	appConfig: PropTypes.object
};

export default SimplyBookWidget;
